import React, { useEffect, useMemo, useState } from 'react';
import './RegisterAppScreen.css';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import backIcon from '../assets/backspace-icon.png';
import placeholderLogo from '../assets/placeholder-logo.png';
import placeholderBanner from '../assets/placeholder-banner.png';
import { uploadToSpaces } from '../utils/uploadToSpaces';
import { compressImage } from '../utils/compressImage';

// ✅ NEW: wallet gate popup for app registration
import AppWalletRequiredPrompt from '../components/popups/AppWalletRequiredPrompt';

const CATEGORY_OPTIONS = [
  'Commerce',
  'Games',
  'NFT',
  'Social',
  'Education',
  'Health',
  'Travel',
  'Utility',
  'Career',
  'Entertainment',
  'Finance',
  'Lifestyle',
  'Music',
  'Productivity',
  'Sports',
];

const INITIAL_FORM = {
  name: '',
  username: '',
  bio: '',
  profilePic: '',
  bannerImage: '',
  appUrl: '',
  domain: '',
  category: 'Utility',
  isPublic: true,
};

function normalizeDomain(value = '') {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '')
    .split('/')[0];
}

function buildValidationFileFromApp(app) {
  const cleanDomain = normalizeDomain(app?.domain || app?.appUrl || '');
  const validationKey = app?.validationKey || '';

  if (!cleanDomain || !validationKey) return null;

  return {
    fileName: 'barterfi-app-validation.txt',
    url: `https://${cleanDomain}/barterfi-app-validation.txt`,
    content: validationKey,
  };
}

function mapAppToForm(app) {
  return {
    name: app?.name || '',
    username: app?.username || '',
    bio: app?.bio || '',
    profilePic: app?.profilePic || '',
    bannerImage: app?.bannerImage || '',
    appUrl: app?.appUrl || '',
    domain: app?.domain || '',
    category: app?.category || 'Utility',
    isPublic: typeof app?.isPublic === 'boolean' ? app.isPublic : true,
  };
}

function RegisterAppScreen({ user }) {
  const navigate = useNavigate();
  const location = useLocation();

  const isAppAccount = user?.accountType === 'app';

  const draftId = location.state?.draftId || '';
  const isDraftEditMode = location.state?.mode === 'draft-edit';

  const DRAFT_KEY = `bf:appRegisterDraft:${user?._id || 'anon'}:${draftId || 'new'}`;

  const [form, setForm] = useState(INITIAL_FORM);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [createdApp, setCreatedApp] = useState(null);
  const [editingDraftId, setEditingDraftId] = useState('');

  const [validationFile, setValidationFile] = useState(null);

  const [verifying, setVerifying] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState('');
  const [verified, setVerified] = useState(false);

  const [profileUploading, setProfileUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);

  // ✅ NEW: wallet gate state
  const [showWalletGate, setShowWalletGate] = useState(false);
  const [checkingWallet, setCheckingWallet] = useState(false);

  const appId = createdApp?._id || editingDraftId || draftId || '';
  const effectiveApp = createdApp || null;

  const screenTitle = useMemo(() => {
    if (editingDraftId || draftId || isDraftEditMode) return 'Continue App Setup';
    return 'Register New App';
  }, [editingDraftId, draftId, isDraftEditMode]);

  const currentStatus = String(effectiveApp?.status || 'draft').toLowerCase();
  const currentVerification = String(
    effectiveApp?.domainVerificationStatus || (verified ? 'verified' : 'pending')
  ).toLowerCase();

  // ✅ Host/network logic same pattern as RegisterMerchant
  const host = window.location.hostname || '';
  const isTestnetHost = host.includes('testnet.');
  const isMainnetHost = !isTestnetHost && host.includes('barterfi.app');

  const MIN_MAINNET_ACTIVATION_PI = Number(
    process.env.REACT_APP_MIN_MAINNET_ACTIVATION_PI || 0.1
  );

  const showError = (msg) => {
    setSuccess('');
    setError(msg);
    setTimeout(() => setError(''), 2500);
  };

  const showSuccess = (msg) => {
    setError('');
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  };

  // ✅ Restore draft for NEW app creation only
  useEffect(() => {
    if (draftId) return;

    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return;

      const saved = JSON.parse(raw);

      if (saved?.form) {
        setForm((prev) => ({
          ...prev,
          ...saved.form,
        }));
      }

      setTermsAccepted(Boolean(saved?.termsAccepted));
    } catch {
      // ignore
    }
  }, [DRAFT_KEY, draftId]);

  // ✅ Save draft for NEW app creation only
  useEffect(() => {
    if (draftId) return;

    try {
      sessionStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({
          form,
          termsAccepted,
        })
      );
    } catch {
      // ignore
    }
  }, [DRAFT_KEY, draftId, form, termsAccepted]);

  useEffect(() => {
    const loadDraft = async () => {
      if (!draftId) return;

      try {
        setLoadingDraft(true);
        setError('');
        setSuccess('');
        setVerifyMessage('');

        const res = await api.get('/apps/me');
        const apps = Array.isArray(res.data?.appProfiles) ? res.data.appProfiles : [];
        const draftApp = apps.find((app) => app._id === draftId);

        if (!draftApp) {
          showError('App draft not found.');
          return;
        }

        setEditingDraftId(draftApp._id);
        setCreatedApp(draftApp);
        setForm(mapAppToForm(draftApp));
        setValidationFile(buildValidationFileFromApp(draftApp));
        setVerified(String(draftApp.domainVerificationStatus || '').toLowerCase() === 'verified');
        setTermsAccepted(true);
      } catch (err) {
        console.error('❌ Failed to load draft app:', err);
        showError(err?.response?.data?.message || 'Failed to load app draft.');
      } finally {
        setLoadingDraft(false);
      }
    };

    loadDraft();
  }, [draftId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let nextValue = type === 'checkbox' ? checked : value;

    if (name === 'username') {
      nextValue = nextValue.toLowerCase().replace(/[^a-z0-9_]/g, '');
    }

    if (name === 'domain') {
      nextValue = normalizeDomain(nextValue);
    }

    setForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const handleProfileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setProfileUploading(true);
      setError('');
      setSuccess('');

      const compressed = await compressImage(file);
      const url = await uploadToSpaces(compressed, 'app-profile-pics');

      setForm((prev) => ({
        ...prev,
        profilePic: url,
      }));

      showSuccess('Profile image uploaded successfully.');
    } catch (err) {
      console.error('❌ Profile upload failed:', err);
      showError('Failed to upload profile image. Please try again.');
    } finally {
      setProfileUploading(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setBannerUploading(true);
      setError('');
      setSuccess('');

      const compressed = await compressImage(file, 0.8);
      const url = await uploadToSpaces(compressed, 'app-banners');

      setForm((prev) => ({
        ...prev,
        bannerImage: url,
      }));

      showSuccess('Banner image uploaded successfully.');
    } catch (err) {
      console.error('❌ Banner upload failed:', err);
      showError('Failed to upload banner image. Please try again.');
    } finally {
      setBannerUploading(false);
    }
  };

  // ✅ NEW: wallet activation hard check
  const checkWalletActivationBeforeSubmit = async () => {
    if (!isMainnetHost) return true;

    const stored = (() => {
      try {
        return JSON.parse(localStorage.getItem('barterfi_user'));
      } catch {
        return null;
      }
    })();

    const username = user?.username || stored?.username;
    if (!username) return true;

    try {
      setCheckingWallet(true);

      const res = await api.get(`/wallet/${username}?network=mainnet`);
      const history = res.data?.history || [];

      const depositTotal = history
        .filter((tx) => ['deposit', 'topup'].includes(tx.type))
        .reduce((sum, tx) => sum + Number(tx.amount || 0), 0);

      const inactive = depositTotal < MIN_MAINNET_ACTIVATION_PI;

      if (inactive) {
        setShowWalletGate(true);
        return false;
      }

      return true;
    } catch (err) {
      console.warn('[RegisterAppScreen] wallet activation check failed:', err);
      setShowWalletGate(true);
      return false;
    } finally {
      setCheckingWallet(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isAppAccount) {
      showError('App accounts cannot register new app profiles. Please switch to your main account.');
      return;
    }

    if (!form.name.trim()) {
      showError('App name is required.');
      return;
    }

    if (!form.username.trim()) {
      showError('App username is required.');
      return;
    }

    const normalizedDomain = normalizeDomain(form.domain || form.appUrl || '');

    if (!normalizedDomain) {
      showError('Domain is required.');
      return;
    }

    if (!form.category.trim()) {
      showError('Please select an app category.');
      return;
    }

    if (!termsAccepted) {
      showError('You must agree to the App Account Terms & Conditions.');
      return;
    }

    const walletOk = await checkWalletActivationBeforeSubmit();
    if (!walletOk) return;

    setLoading(true);
    setError('');
    setSuccess('');
    setVerifyMessage('');

    try {
      const payload = {
        ...form,
        domain: normalizedDomain,
      };

      let res;

      if (editingDraftId) {
        res = await api.patch(`/apps/${editingDraftId}`, payload);
      } else {
        res = await api.post('/apps', payload);
      }

      const appProfile = res.data?.appProfile || null;
      const returnedValidationFile = res.data?.validationFile || null;
      const rebuiltValidationFile = appProfile ? buildValidationFileFromApp(appProfile) : null;

      setCreatedApp(appProfile);
      setEditingDraftId(appProfile?._id || editingDraftId || '');
      setForm(mapAppToForm(appProfile || payload));
      setValidationFile(returnedValidationFile || rebuiltValidationFile || null);
      setVerified(String(appProfile?.domainVerificationStatus || '').toLowerCase() === 'verified');

      if (!editingDraftId) {
        try {
          sessionStorage.removeItem(DRAFT_KEY);
        } catch {
          // ignore
        }
      }

      if (String(appProfile?.domainVerificationStatus || '').toLowerCase() === 'verified') {
        showSuccess('Domain already verified.');
        setVerifyMessage('Domain already verified.');
      } else {
        showSuccess(
          editingDraftId
            ? 'App profile updated successfully.'
            : 'App profile created successfully.'
        );
        setVerifyMessage('App profile saved. Next, verify your domain to activate the app.');
      }
    } catch (err) {
      console.error('❌ Failed to save app profile:', err);
      showError(err?.response?.data?.message || 'Failed to save app profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      showSuccess('Copied successfully.');
    } catch (err) {
      console.error('❌ Copy failed:', err);
      showError('Failed to copy. Please try again.');
    }
  };

  const handleVerify = async () => {
    if (isAppAccount) {
      showError('App accounts cannot manage app registration. Please switch to your main account.');
      return;
    }

    const walletOk = await checkWalletActivationBeforeSubmit();
    if (!walletOk) return;

    if (!appId) {
      showError('App profile not found.');
      return;
    }

    setVerifying(true);
    setVerifyMessage('');
    setError('');
    setSuccess('');

    try {
      const res = await api.post(`/apps/${appId}/verify-domain`);
      const updated = res.data?.appProfile || null;

      setCreatedApp(updated);
      setEditingDraftId(updated?._id || editingDraftId || '');
      setForm(mapAppToForm(updated || form));
      setValidationFile(res.data?.validationFile || buildValidationFileFromApp(updated));
      setVerified(String(updated?.domainVerificationStatus || '').toLowerCase() === 'verified');

      if (String(updated?.domainVerificationStatus || '').toLowerCase() === 'verified') {
        setVerifyMessage('Domain verified successfully. Your app is now active.');
        showSuccess('Domain verified successfully. Your app is now active.');
      } else {
        setVerifyMessage('Verification completed, but the app is not marked verified yet.');
        showError('Verification completed, but the app is not marked verified yet.');
      }
    } catch (err) {
      console.error('❌ Domain verification failed:', err);
      setVerified(false);

      const msg =
        err?.response?.data?.message ||
        'Verification failed. Please check the validation file and try again.';

      setVerifyMessage(msg);
      showError(msg);
    } finally {
      setVerifying(false);
    }
  };

  const handleBackToApps = () => {
    navigate('/my-apps');
  };

  return (
    <>
      <div className="registerapp-screen">
        {error && <div className="registerapp-banner error">{error}</div>}
        {success && <div className="registerapp-banner success">{success}</div>}

        <div className="registerapp-topbar">
          <button className="registerapp-back-btn" onClick={() => navigate(-1)}>
            <img src={backIcon} alt="Back" />
          </button>
          <h1>{screenTitle}</h1>
        </div>

        <div className={`registerapp-content ${error || success ? 'has-banner' : ''}`}>
          {loadingDraft && (
            <div className="registerapp-card">
              <p>Loading app draft...</p>
            </div>
          )}

          {!loadingDraft && (
            <>
              {isAppAccount ? (
                <div className="registerapp-card">
                  <h2>App Registration Unavailable</h2>
                  <p className="registerapp-block-text">
                    App accounts cannot register other app accounts.
                  </p>
                  <p className="registerapp-block-subtext">
                    Please switch back to your main account to create or manage app profiles.
                  </p>

                  {error ? <p className="registerapp-error">{error}</p> : null}

                  <button
                    type="button"
                    className="registerapp-secondary-btn"
                    onClick={() => navigate(-1)}
                  >
                    Go Back
                  </button>
                </div>
              ) : (
                <form className="registerapp-form" onSubmit={handleSubmit}>
                  <div className="registerapp-card">
                    <h2>{editingDraftId ? 'Edit App Details' : 'App Details'}</h2>

                    <label>
                      App Name <span className="req">*</span>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="BarterFi AI"
                        required
                      />
                    </label>

                    <label>
                      App Username <span className="req">*</span>
                      <input
                        type="text"
                        name="username"
                        value={form.username}
                        onChange={handleChange}
                        placeholder="barterfi_ai"
                        required
                      />
                    </label>

                    <label>
                      Bio
                      <textarea
                        name="bio"
                        value={form.bio}
                        onChange={handleChange}
                        placeholder="Short description about your app"
                        rows={4}
                      />
                    </label>

                    <label>
                      App URL
                      <input
                        type="text"
                        name="appUrl"
                        value={form.appUrl}
                        onChange={handleChange}
                        placeholder="https://yourapp.com"
                      />
                    </label>

                    <label>
                      Domain <span className="req">*</span>
                      <input
                        type="text"
                        name="domain"
                        value={form.domain}
                        onChange={handleChange}
                        placeholder="yourapp.com"
                        required
                      />
                    </label>

                    <label>
                      Category <span className="req">*</span>
                      <select
                        name="category"
                        value={form.category}
                        onChange={handleChange}
                      >
                        {CATEGORY_OPTIONS.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </label>

                    <div className="registerapp-upload-block">
                      <label className="registerapp-upload-label">
                        Profile Image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleProfileUpload}
                        />
                      </label>

                      <div className="registerapp-logo-preview-shell">
                        {profileUploading ? (
                          <div className="registerapp-upload-loading">Uploading profile image...</div>
                        ) : (
                          <img
                            src={form.profilePic || placeholderLogo}
                            alt="App profile preview"
                            className="registerapp-logo-preview"
                          />
                        )}
                      </div>
                    </div>

                    <div className="registerapp-upload-block">
                      <label className="registerapp-upload-label">
                        Banner Image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleBannerUpload}
                        />
                      </label>

                      <div className="registerapp-banner-preview-shell">
                        {bannerUploading ? (
                          <div className="registerapp-upload-loading">Uploading banner image...</div>
                        ) : (
                          <img
                            src={form.bannerImage || placeholderBanner}
                            alt="App banner preview"
                            className="registerapp-banner-preview"
                          />
                        )}
                      </div>
                    </div>

                    <label className="registerapp-checkbox">
                      <input
                        type="checkbox"
                        name="isPublic"
                        checked={form.isPublic}
                        onChange={handleChange}
                      />
                      <span>Make this app public after activation</span>
                    </label>

                    <div className="registerapp-terms-section">
                      <label className="registerapp-terms-check">
                        <input
                          type="checkbox"
                          checked={termsAccepted}
                          onChange={() => setTermsAccepted((prev) => !prev)}
                        />
                        <span>I agree to BarterFi&apos;s App Account Terms &amp; Conditions</span>
                      </label>

                      <button
                        type="button"
                        className="registerapp-terms-link"
                        onClick={() => navigate('/app-terms')}
                      >
                        View Terms
                      </button>
                    </div>

                    <button
                      type="submit"
                      className="registerapp-primary-btn"
                      disabled={loading || checkingWallet || profileUploading || bannerUploading}
                    >
                      {checkingWallet
                        ? 'Checking wallet...'
                        : loading
                        ? editingDraftId
                          ? 'Saving...'
                          : 'Creating...'
                        : editingDraftId
                        ? 'Save App'
                        : 'Create App Profile'}
                    </button>
                  </div>
                </form>
              )}

              {!isAppAccount && appId && validationFile && (
                <div className="registerapp-setup">
                  <div className="registerapp-card">
                    <h2>{verified ? 'App Verified' : 'Next Step: Verify Your Domain'}</h2>

                    <p>
                      {verified
                        ? 'Your domain is verified and your app should now be active.'
                        : 'Your app profile has been saved. To activate it, place the validation file on your domain and then verify.'}
                    </p>

                    <div className="registerapp-status-row">
                      <div className={`mini-badge status-${currentStatus}`}>
                        {currentStatus}
                      </div>
                      <div className={`mini-badge verify ${currentVerification}`}>
                        {currentVerification}
                      </div>
                    </div>

                    {effectiveApp?.linkedUser ? (
                      <p className="registerapp-success">
                        Linked sub-account created successfully.
                      </p>
                    ) : null}
                  </div>

                  <div className="registerapp-card">
                    <h2>Step 1: Create this file on your domain</h2>

                    <label>
                      File name
                      <div className="copy-box">
                        <span>{validationFile.fileName}</span>
                        <button
                          type="button"
                          onClick={() => handleCopy(validationFile.fileName)}
                        >
                          Copy
                        </button>
                      </div>
                    </label>

                    <label>
                      Upload it here
                      <div className="copy-box">
                        <span>{validationFile.url}</span>
                        <button
                          type="button"
                          onClick={() => handleCopy(validationFile.url)}
                        >
                          Copy
                        </button>
                      </div>
                    </label>

                    <label>
                      File content
                      <div className="copy-box">
                        <span className="code-text">{validationFile.content}</span>
                        <button
                          type="button"
                          onClick={() => handleCopy(validationFile.content)}
                        >
                          Copy
                        </button>
                      </div>
                    </label>
                  </div>

                  <div className="registerapp-card">
                    <h2>Step 2: Verify Domain</h2>

                    <p>
                      After uploading <strong>barterfi-app-validation.txt</strong> to your domain root, click verify.
                    </p>

                    {verifyMessage ? (
                      <p className={verified ? 'registerapp-success' : 'registerapp-error'}>
                        {verifyMessage}
                      </p>
                    ) : null}

                    {!verified && (
                      <button
                        type="button"
                        className="registerapp-primary-btn"
                        onClick={handleVerify}
                        disabled={verifying || checkingWallet}
                      >
                        {checkingWallet
                          ? 'Checking wallet...'
                          : verifying
                          ? 'Verifying...'
                          : 'Verify Domain'}
                      </button>
                    )}

                    <button
                      type="button"
                      className="registerapp-secondary-btn"
                      onClick={handleBackToApps}
                    >
                      Back to My Apps
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showWalletGate && (
        <AppWalletRequiredPrompt
          threshold={MIN_MAINNET_ACTIVATION_PI}
          onClose={() => setShowWalletGate(false)}
          onActivate={() => {
            setShowWalletGate(false);
            navigate('/my-wallet');
          }}
        />
      )}
    </>
  );
}

export default RegisterAppScreen;
